const STORAGE_KEY = "jfl_utms_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

/**
 * Returns the current UTM parameters for this visitor.
 * - First-visit UTMs are captured from the URL and persisted to sessionStorage
 *   so they survive route changes (e.g. /divorce, /privacy-policy) and form
 *   focus/blur. We also append `referrer` (best-effort) for organic traffic.
 * - Subsequent reads return the same captured set.
 * - Returns a plain object with stringified values; keys without a value are
 *   left as empty strings.
 */
export function getUTMs() {
  if (typeof window === "undefined") return blankUTMs();

  // 1. Capture from current URL (overrides if a fresh visit lands with new utms).
  let captured = readFromSession();
  const fromURL = readFromURL();
  const hasFreshUTM = UTM_KEYS.some((k) => !!fromURL[k]);

  if (hasFreshUTM) {
    captured = { ...blankUTMs(), ...fromURL };
    captured.referrer = safeReferrer();
    captured.first_landing = window.location.pathname || "/";
    captured.captured_at = new Date().toISOString();
    writeToSession(captured);
  } else if (!captured) {
    // No URL UTMs and no prior session — store a baseline with just referrer.
    captured = {
      ...blankUTMs(),
      referrer: safeReferrer(),
      first_landing: window.location.pathname || "/",
      captured_at: new Date().toISOString(),
    };
    writeToSession(captured);
  }

  return captured;
}

function blankUTMs() {
  return {
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
    referrer: "",
    first_landing: "",
    captured_at: "",
  };
}

function readFromURL() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const out = {};
    UTM_KEYS.forEach((k) => {
      out[k] = (sp.get(k) || "").trim();
    });
    return out;
  } catch {
    return {};
  }
}

function readFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeToSession(obj) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* storage disabled — ignore */
  }
}

function safeReferrer() {
  try {
    const ref = document.referrer || "";
    // Don't store our own host as the referrer.
    if (ref && !ref.includes(window.location.host)) return ref;
    return "";
  } catch {
    return "";
  }
}
